import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import { getScopedProviderOptions } from 'api/group';
import { TScopedProviderOption } from 'types/group';
import { generateKlarityReviewLink } from 'utils/common';
import PatientPreview from './components/PatientPreview';
import {
    CHANNELS,
    EChannel,
    GUIDE_COPY,
    PATIENT_NAME_TOKEN,
    PREVIEW_CAPS,
    TGuideProvider,
    getChannel,
} from './constants';
import s from './s.module.less';

type Props = {
    open: boolean;
    onClose: () => void;
    // group practice admins pick which provider's link they are sharing
    showProviderPicker?: boolean;
    // current user's public uid, used to build his/her own review link
    uidPublic?: string;
    selfName?: string;
    selfCredential?: string;
    // the current user's specialty line, mirrored on the public profile preview
    selfSpecialty?: string;
    // preselect the provider the admin just copied a link for
    initialProviderId?: number;
};

const COPIED_RESET_DELAY = 1800;

const getOptionName = (option: TScopedProviderOption) =>
    `${option.firstName || ''} ${option.lastName || ''}`.trim() || 'Unnamed provider';

const getInitials = (name: string) =>
    name
        .split(' ')
        .filter(Boolean)
        .map((n) => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);

const escapeRegExp = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const CopyIcon = () => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="5" y="5" width="9" height="9" rx="1.5" />
        <path d="M3 11V3a2 2 0 012-2h8" />
    </svg>
);

const CheckIcon = () => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="3 8.5 6.5 12 13 4" />
    </svg>
);

// the outcome list uses a circled tick, not the bare one on the copy buttons
const CheckCircleIcon = () => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="8" cy="8" r="6.5" />
        <polyline points="5 8.3 7 10.3 11 5.8" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="3" y1="8" x2="12" y2="8" />
        <polyline points="8.5 4.5 12 8 8.5 11.5" />
    </svg>
);

const ReviewLinkGuideModal: React.FC<Props> = ({
    open,
    onClose,
    showProviderPicker,
    uidPublic,
    selfName,
    selfCredential,
    selfSpecialty,
    initialProviderId,
}) => {
    const navigate = useNavigate();
    const [options, setOptions] = useState<TScopedProviderOption[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [selectedId, setSelectedId] = useState<number>();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [channel, setChannel] = useState<EChannel>(EChannel.MESSAGE);
    const [previewIdx, setPreviewIdx] = useState(0);
    const [linkCopied, setLinkCopied] = useState(false);
    const [msgCopied, setMsgCopied] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const linkTimer = useRef<ReturnType<typeof setTimeout>>();
    const msgTimer = useRef<ReturnType<typeof setTimeout>>();

    const copyMode = showProviderPicker ? 'group' : 'solo';
    const copyText = GUIDE_COPY[copyMode];

    const fetchOptions = useCallback(async () => {
        setLoadingOptions(true);
        try {
            const res = await getScopedProviderOptions({ includeSelf: true });
            if (res && !res.error) {
                const list: TScopedProviderOption[] = res.data?.data || [];
                // current user first, matching the prototype
                const sorted = [...list].sort((a, b) => Number(!!b.isSelf) - Number(!!a.isSelf));
                setOptions(sorted);
            }
        } catch (e) {
            console.error(e);
        }
        setLoadingOptions(false);
    }, []);

    useEffect(() => {
        if (open && showProviderPicker) {
            fetchOptions();
        }
    }, [open, showProviderPicker, fetchOptions]);

    // every open starts the walkthrough from the top
    useEffect(() => {
        if (open) {
            setChannel(EChannel.MESSAGE);
            setPreviewIdx(0);
            setKeyword('');
            setPickerOpen(false);
            setLinkCopied(false);
            setMsgCopied(false);
        }
    }, [open]);

    useEffect(() => {
        if (open && initialProviderId) {
            setSelectedId(initialProviderId);
        }
    }, [open, initialProviderId]);

    // default to the current user once the roster lands
    useEffect(() => {
        if (!options.length) {
            return;
        }
        setSelectedId((prev) => {
            if (prev && options.some((item) => item.providerId === prev)) {
                return prev;
            }
            return options.find((item) => item.isSelf)?.providerId ?? options[0]?.providerId;
        });
    }, [options]);

    useEffect(
        () => () => {
            clearTimeout(linkTimer.current);
            clearTimeout(msgTimer.current);
        },
        [],
    );

    useEffect(() => {
        if (!pickerOpen) {
            return undefined;
        }
        const onDocClick = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [pickerOpen]);

    const selectedOption = useMemo(() => options.find((item) => item.providerId === selectedId), [options, selectedId]);

    const filteredOptions = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        if (!q) {
            return options;
        }
        return options.filter(
            (option) =>
                getOptionName(option).toLowerCase().includes(q) || (option.credential || '').toLowerCase().includes(q),
        );
    }, [options, keyword]);

    const ownLink = generateKlarityReviewLink(uidPublic);

    const guideProvider: TGuideProvider & { credential?: string; specialty?: string; avatar?: string } = useMemo(() => {
        if (!showProviderPicker) {
            return {
                name: selfName || 'you',
                credential: selfCredential,
                specialty: selfSpecialty,
                avatar: undefined,
                link: ownLink,
            };
        }
        if (!selectedOption) {
            return { name: selfName || '', credential: selfCredential, link: '' };
        }
        return {
            name: getOptionName(selectedOption),
            credential: selectedOption.credential,
            // the roster endpoint doesn't carry specialties, so only the
            // current user's own preview can show that line
            specialty: selectedOption.isSelf ? selfSpecialty : undefined,
            avatar: selectedOption.avatar || undefined,
            link: selectedOption.isSelf
                ? ownLink
                : (selectedOption.uidPublic && generateKlarityReviewLink(selectedOption.uidPublic)) || '',
        };
    }, [showProviderPicker, selectedOption, selfName, selfCredential, selfSpecialty, ownLink]);

    const currentChannel = getChannel(channel);
    const templateText = guideProvider.link ? currentChannel.text(guideProvider) : '';

    const handleCopyLink = () => {
        if (!guideProvider.link) {
            message.error('This provider does not have a review link yet.');
            return;
        }
        copy(guideProvider.link, { format: 'text/plain' });
        message.success('Review link copied');
        setLinkCopied(true);
        clearTimeout(linkTimer.current);
        linkTimer.current = setTimeout(() => setLinkCopied(false), COPIED_RESET_DELAY);
    };

    const handleCopyMessage = () => {
        if (!templateText) {
            message.error('This provider does not have a review link yet.');
            return;
        }
        copy(templateText, { format: 'text/plain' });
        // "Messages → patient thread" reads better as just "Messages" in a toast
        message.success(`Message + link copied. Paste it into ${currentChannel.where.split(' → ')[0]}.`);
        setMsgCopied(true);
        clearTimeout(msgTimer.current);
        msgTimer.current = setTimeout(() => setMsgCopied(false), COPIED_RESET_DELAY);
    };

    const showPreview = useCallback((i: number) => {
        const len = PREVIEW_CAPS.length;
        setPreviewIdx(((i % len) + len) % len);
    }, []);

    // arrow keys step through the patient-side preview
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') {
                setPreviewIdx((prev) => (prev + 1) % PREVIEW_CAPS.length);
            }
            if (e.key === 'ArrowLeft') {
                setPreviewIdx((prev) => (prev - 1 + PREVIEW_CAPS.length) % PREVIEW_CAPS.length);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open]);

    // highlight the merge token and the link so it's obvious what to personalize
    const templateNodes = useMemo(() => {
        if (!templateText) {
            return null;
        }
        const alternatives = [escapeRegExp(PATIENT_NAME_TOKEN)];
        if (guideProvider.link) {
            alternatives.push(escapeRegExp(guideProvider.link));
        }
        const pattern = new RegExp(`(${alternatives.join('|')})`, 'g');
        return templateText.split(pattern).map((chunk, i) => {
            const key = `${i}-${chunk}`;
            if (chunk === PATIENT_NAME_TOKEN) {
                return (
                    <span key={key} className={s.tok}>
                        Patient first name
                    </span>
                );
            }
            if (guideProvider.link && chunk === guideProvider.link) {
                return (
                    <span key={key} className={s.tokLink}>
                        {chunk}
                    </span>
                );
            }
            return <React.Fragment key={key}>{chunk}</React.Fragment>;
        });
    }, [templateText, guideProvider.link]);

    const stepNo = (n: number) => (showProviderPicker ? n : n - 1);

    const caps = PREVIEW_CAPS[previewIdx];
    const capDesc = previewIdx === PREVIEW_CAPS.length - 1 ? copyText.liveCap : caps.d;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={1000}
            centered
            destroyOnClose
            maskClosable
            className={s.guideModal}
            title={
                <div className={s.hdr}>
                    <div className={s.hdrTitle}>How to use your review link</div>
                    <div className={s.hdrSub}>
                        {guideProvider.name ? (
                            <>
                                Collecting reviews for <strong>{guideProvider.name}</strong> ·{' '}
                                {showProviderPicker ? 'about' : 'takes about'} 30 seconds per patient
                            </>
                        ) : (
                            'Pick a provider, copy their link, send it to their patients — about 30 seconds each.'
                        )}
                    </div>
                </div>
            }
            footer={
                <div className={s.ft}>
                    <div className={s.ftTip}>
                        <svg
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M6 12.5h4M6.5 14.5h3" />
                            <path d="M8 1.5a4.3 4.3 0 00-2.5 7.8c.3.3.5.7.5 1.2h4c0-.5.2-.9.5-1.2A4.3 4.3 0 008 1.5z" />
                        </svg>
                        <span>Best results: send within 24 hours of the visit, while it&apos;s still fresh.</span>
                    </div>
                    <Button type="primary" className={s.btnDone} onClick={onClose}>
                        Got it
                    </Button>
                </div>
            }
        >
            <div className={s.body}>
                <div className={s.left}>
                    {showProviderPicker && (
                        <div className={s.step}>
                            <div className={s.stepRail}>
                                <div className={s.stepDot}>1</div>
                                <div className={s.stepLine} />
                            </div>
                            <div className={s.stepMain}>
                                <div className={s.stepTitle}>Choose a provider to collect reviews for</div>
                                <div className={s.stepDesc}>
                                    Every provider in the practice has their own link — reviews land on that
                                    provider&apos;s Klarity profile.
                                </div>
                                <div className={`${s.select} ${pickerOpen ? s.selectOpen : ''}`} ref={pickerRef}>
                                    <button
                                        type="button"
                                        className={s.selectBtn}
                                        onClick={() => {
                                            setPickerOpen((v) => {
                                                if (!v) {
                                                    // typing is the fastest way through a long roster
                                                    setTimeout(() => searchRef.current?.focus(), 0);
                                                }
                                                return !v;
                                            });
                                            setKeyword('');
                                        }}
                                    >
                                        {selectedOption?.avatar ? (
                                            <img className={s.selectAvatar} src={selectedOption.avatar} alt="" />
                                        ) : (
                                            <span className={s.selectAvatar}>
                                                {getInitials(guideProvider.name || '')}
                                            </span>
                                        )}
                                        <span className={s.selectInfo}>
                                            <span className={s.selectName}>
                                                {guideProvider.name}
                                                {selectedOption?.isSelf ? ' (You)' : ''}
                                            </span>
                                            <span className={s.selectCred}>{guideProvider.credential}</span>
                                        </span>
                                        <svg
                                            className={s.selectChev}
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="4 6 8 10 12 6" />
                                        </svg>
                                    </button>
                                    <div className={s.selectDd}>
                                        <div className={s.pickerSearch}>
                                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="7" cy="7" r="5" />
                                                <line x1="11" y1="11" x2="14" y2="14" strokeLinecap="round" />
                                            </svg>
                                            <input
                                                ref={searchRef}
                                                type="text"
                                                placeholder="Search providers…"
                                                value={keyword}
                                                onChange={(e) => setKeyword(e.target.value)}
                                            />
                                        </div>
                                        <Spin spinning={loadingOptions}>
                                            <div className={s.pickerList}>
                                                {filteredOptions.map((option) => (
                                                    <div
                                                        key={option.providerId}
                                                        className={`${s.pickerItem} ${
                                                            option.providerId === selectedId ? s.pickerItemSelected : ''
                                                        }`}
                                                        onClick={() => {
                                                            setSelectedId(option.providerId);
                                                            setPickerOpen(false);
                                                        }}
                                                    >
                                                        {option.avatar ? (
                                                            <img
                                                                className={s.pickerAvatar}
                                                                src={option.avatar}
                                                                alt=""
                                                            />
                                                        ) : (
                                                            <span className={s.pickerAvatar}>
                                                                {getInitials(getOptionName(option))}
                                                            </span>
                                                        )}
                                                        <div className={s.pickerInfo}>
                                                            <div className={s.pickerName}>
                                                                {getOptionName(option)}
                                                                {option.isSelf && (
                                                                    <span className={s.pickerYou}>(You)</span>
                                                                )}
                                                            </div>
                                                            <div className={s.pickerCred}>{option.credential}</div>
                                                        </div>
                                                        <span className={s.pickerCheck}>
                                                            <CheckIcon />
                                                        </span>
                                                    </div>
                                                ))}
                                                {!loadingOptions && filteredOptions.length === 0 && (
                                                    <div className={s.pickerEmpty}>No providers match that search.</div>
                                                )}
                                            </div>
                                        </Spin>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={s.step}>
                        <div className={s.stepRail}>
                            <div className={s.stepDot}>{stepNo(2)}</div>
                            <div className={s.stepLine} />
                        </div>
                        <div className={s.stepMain}>
                            <div className={s.stepTitle}>{copyText.titleCopy}</div>
                            <div className={s.stepDesc}>{copyText.descCopy}</div>
                            <div className={s.linkrow}>
                                <div className={s.linkbox}>
                                    <svg
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.6"
                                        strokeLinecap="round"
                                    >
                                        <path d="M6.5 9.5a2.8 2.8 0 004.1.3l2.1-2.1a2.8 2.8 0 00-4-4l-1.1 1.2" />
                                        <path d="M9.5 6.5a2.8 2.8 0 00-4.1-.3l-2.1 2.1a2.8 2.8 0 004 4l1.1-1.2" />
                                    </svg>
                                    <span>{guideProvider.link || '—'}</span>
                                </div>
                                <Button
                                    type="primary"
                                    className={`${s.copyBtn} ${linkCopied ? s.copyBtnDone : ''}`}
                                    onClick={handleCopyLink}
                                >
                                    {linkCopied ? <CheckIcon /> : <CopyIcon />}
                                    {linkCopied ? 'Copied' : 'Copy link'}
                                </Button>
                            </div>
                            <div className={s.note}>
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <circle cx="8" cy="8" r="6.5" />
                                    <line x1="8" y1="7.2" x2="8" y2="11.5" strokeLinecap="round" />
                                    <circle cx="8" cy="4.9" r=".7" fill="currentColor" stroke="none" />
                                </svg>
                                <span>
                                    Safe to share — the link only opens a review form. It never exposes any patient
                                    record.
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={s.step}>
                        <div className={s.stepRail}>
                            <div className={s.stepDot}>{stepNo(3)}</div>
                            <div className={s.stepLine} />
                        </div>
                        <div className={s.stepMain}>
                            <div className={s.stepTitle}>Paste it where you already talk to your patients</div>
                            <div className={s.stepDesc}>
                                Pick a channel and copy a ready-to-send message — link included.
                            </div>
                            <div className={s.chanTabs}>
                                {CHANNELS.map((item) => (
                                    <button
                                        type="button"
                                        key={item.key}
                                        className={`${s.chan} ${channel === item.key ? s.chanActive : ''}`}
                                        onClick={() => {
                                            setChannel(item.key);
                                            setMsgCopied(false);
                                        }}
                                    >
                                        {item.icon}
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                            <div className={s.tmpl}>
                                <div className={s.tmplHd}>
                                    <span className={s.tmplLabel}>{currentChannel.label}</span>
                                    <span className={s.tmplWhere}>{currentChannel.where}</span>
                                </div>
                                <div className={s.tmplBody}>{templateNodes}</div>
                                <div className={s.tmplActs}>
                                    <Button
                                        type="primary"
                                        className={`${s.btnSm} ${msgCopied ? s.btnDoneState : ''}`}
                                        onClick={handleCopyMessage}
                                    >
                                        {msgCopied ? <CheckIcon /> : <CopyIcon />}
                                        {msgCopied ? 'Copied — now paste it' : 'Copy message + link'}
                                    </Button>
                                    {currentChannel.href && (
                                        <Button
                                            className={s.btnSm}
                                            onClick={() => {
                                                onClose();
                                                navigate(currentChannel.href!);
                                            }}
                                        >
                                            {currentChannel.cta}
                                            <ArrowRightIcon />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={s.step}>
                        <div className={s.stepRail}>
                            <div className={s.stepDot}>{stepNo(4)}</div>
                        </div>
                        <div className={s.stepMain}>
                            <div className={s.stepTitle}>{copyText.titleOutcome}</div>
                            <div className={s.out}>
                                <div className={s.outItem}>
                                    <CheckCircleIcon />
                                    <span>
                                        They sign in with the phone or email on file, so the review lands as a{' '}
                                        <b>Verified patient</b>.
                                    </span>
                                </div>
                                <div className={s.outItem}>
                                    <CheckCircleIcon />
                                    <span>{copyText.outProfile}</span>
                                </div>
                                <div className={s.outItem}>
                                    <CheckCircleIcon />
                                    {/* eslint-disable-next-line react/no-danger */}
                                    <span dangerouslySetInnerHTML={{ __html: copyText.outDispute }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={s.right}>
                    <div className={s.prevTitle}>What your patient sees</div>
                    <div className={s.prevSub}>Preview of the flow after they tap your link</div>
                    <PatientPreview
                        name={guideProvider.name}
                        credential={guideProvider.credential}
                        specialty={guideProvider.specialty}
                        avatar={guideProvider.avatar}
                        link={guideProvider.link}
                        activeIndex={previewIdx}
                    />
                    <div className={s.prevNav}>
                        <button
                            type="button"
                            className={s.navBtn}
                            aria-label="Previous"
                            onClick={() => showPreview(previewIdx - 1)}
                        >
                            <svg
                                viewBox="0 0 16 16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="10 3 5 8 10 13" />
                            </svg>
                        </button>
                        <div className={s.dots}>
                            {PREVIEW_CAPS.map((cap, i) => (
                                <button
                                    type="button"
                                    key={cap.t}
                                    aria-label={`Step ${i + 1}`}
                                    className={`${s.dot} ${i === previewIdx ? s.dotActive : ''}`}
                                    onClick={() => showPreview(i)}
                                />
                            ))}
                        </div>
                        <button
                            type="button"
                            className={s.navBtn}
                            aria-label="Next"
                            onClick={() => showPreview(previewIdx + 1)}
                        >
                            <svg
                                viewBox="0 0 16 16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="6 3 11 8 6 13" />
                            </svg>
                        </button>
                    </div>
                    <div className={s.prevCap}>
                        <div className={s.prevCapT}>{caps.t}</div>
                        <div className={s.prevCapD}>{capDesc}</div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ReviewLinkGuideModal;
